import '@/integrations/firebase'

import { useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { useMe } from '@/api/hooks/useMe'
import { useFirebaseUser } from '@/hooks/useFirebaseUser'

import FullScreenSpinner from './molecules/full-screen-spinner'

const unauthenticatedRoutes = ['/login']
const allowedRoutesWithoutIndividual = ['/onboarding', '/add-individual']

export const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isLoading: isLoadingFirebaseUser, isLoggedIn } = useFirebaseUser()
  const userQuery = useMe(isLoggedIn)
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname

  const isOnUnauthenticatedRoute = unauthenticatedRoutes.some(route => pathname.startsWith(route))
  const requiresIndividual = isLoggedIn && userQuery.data?.individuals.length === 0

  const isLoading = isLoadingFirebaseUser || userQuery.isLoading
  
  useEffect(() => {
    if (isLoading) return

    if (isLoggedIn && pathname === '/') {
      navigate({ replace: true, to: '/home' })

      return
    }

    if (!isLoggedIn && !isOnUnauthenticatedRoute) {
      navigate({ replace: true, to: '/login' })

      return
    }

    if (isLoggedIn && isOnUnauthenticatedRoute) {
      navigate({ replace: true, to: '/' })

      return
    }

    if (requiresIndividual && !allowedRoutesWithoutIndividual.includes(pathname)) {
      navigate({ replace: true, to: '/onboarding' })

      return
    }
  }, [isLoading, isLoggedIn, navigate, pathname, userQuery.data])

  if (isLoading) {
    if (isOnUnauthenticatedRoute) {
      return children
    }

    return <FullScreenSpinner />
  }

  if (!isLoggedIn && !isOnUnauthenticatedRoute) {
    return <FullScreenSpinner />
  }

  if (userQuery.isLoading) {
    return <FullScreenSpinner />
  }

  if (requiresIndividual && !allowedRoutesWithoutIndividual.includes(pathname)) {
    return <FullScreenSpinner />
  }

  return children
}
